<template>
  <div class="page-content">
    <breadcrumb-index :items="breadcrumbs">
      <template #actions>
        <router-link :to="`/app/exams/edit/${examId}`" class="btn btn-outline-secondary btn-sm">
          <vue-feather type="arrow-left" size="14" class="me-1" />
          Back
        </router-link>
        <button @click="openCreateModal" class="btn btn-primary btn-sm">
          <vue-feather type="plus" size="14" class="me-1" />
          Add Test Series
        </button>
      </template>
    </breadcrumb-index>

    <DataTable
      :items="testSeriesList"
      :columns="columns"
      :loading="loading"
      :searchable="false"
      empty-icon="layers"
      empty-title="No test series found"
      empty-description="Create your first test series to get started"
    >
      <template #cell-name="{ item }">
        <div class="avatar-info">
          <span class="avatar-name">{{ item.name }}</span>
          <span class="avatar-subtitle">{{ item.testCount || item.totalTests || 0 }} tests</span>
        </div>
      </template>

      <template #cell-tests="{ item }">
        <router-link :to="`/app/test-series/${item.id}/tests`" class="badge bg-primary text-decoration-none">
          {{ item.testCount || 0 }} tests
        </router-link>
      </template>

      <template #cell-price="{ item }">
        <span v-if="item.isFree" class="badge bg-success">Free</span>
        <span v-else class="fw-medium">₹{{ item.price }}</span>
      </template>

      <template #cell-duration="{ item }">
        <span v-if="item.startDate" class="text-muted">
          {{ formatDate(item.startDate) }} - {{ formatDate(item.endDate) }}
        </span>
        <span v-else class="text-muted">-</span>
      </template>

      <template #cell-status="{ item }">
        <span :class="['status-badge', getStatusClass(item.status)]">
          {{ item.status }}
        </span>
      </template>

      <template #cell-actions="{ item }">
        <div class="action-buttons">
          <router-link 
            :to="`/app/test-series/${item.id}/tests`" 
            class="action-btn action-view" 
            v-tooltip="'View tests'"
          >
            <vue-feather type="check-square" size="14" />
          </router-link>
          <button 
            class="action-btn action-edit" 
            @click="openEditModal(item)"
            v-tooltip="'Edit test series'"
          >
            <vue-feather type="edit-2" size="14" />
          </button>
          <button 
            class="action-btn action-delete" 
            @click="confirmDelete(item)" 
            v-tooltip="{ text: 'Delete test series', variant: 'danger' }"
          >
            <vue-feather type="trash-2" size="14" />
          </button>
        </div>
      </template>

      <template #empty-action>
        <button @click="openCreateModal" class="btn btn-primary">
          <vue-feather type="plus" size="16" class="me-1" />
          Create First Test Series
        </button>
      </template>
    </DataTable>

    <!-- Create/Edit Modal -->
    <CreateModal
      v-model="showModal"
      :title="isEditing ? 'Edit Test Series' : 'Add Test Series'"
      icon="layers"
      size="lg"
      :loading="saving"
      :submit-text="isEditing ? 'Update' : 'Create'"
      :submit-disabled="!form.name"
      :item-id="isEditing ? editingId : ''"
      @submit="saveTestSeries"
    >
      <form @submit.prevent="saveTestSeries" class="modal-form">
        <div class="form-group">
          <label class="form-label">Series Name <span class="text-danger">*</span></label>
          <input 
            v-model="form.name" 
            type="text" 
            class="form-control" 
            placeholder="e.g., UPSC Prelims Mock Test Series 2025"
            required
          />
        </div>

        <div class="form-group">
          <label class="form-label">Description</label>
          <textarea 
            v-model="form.description" 
            class="form-control" 
            rows="3"
            placeholder="Describe this test series..."
          ></textarea>
        </div>

        <div class="form-row">
          <div class="form-group">
            <label class="form-label">Total Tests</label>
            <input 
              v-model.number="form.totalTests" 
              type="number" 
              class="form-control" 
              placeholder="0"
              min="0"
            />
          </div>

          <div class="form-group">
            <label class="form-label">Status</label>
            <select v-model="form.status" class="form-control">
              <option value="draft">Draft</option>
              <option value="active">Active</option>
              <option value="archived">Archived</option>
            </select>
          </div>
        </div>

        <div class="form-group">
          <div class="form-check">
            <input type="checkbox" class="form-check-input" id="isFree" v-model="form.isFree" />
            <label class="form-check-label" for="isFree">This is a free test series</label>
          </div>
        </div>

        <div class="form-group" v-if="!form.isFree">
          <label class="form-label">Price (₹)</label>
          <input 
            v-model.number="form.price" 
            type="number" 
            class="form-control" 
            placeholder="0"
            min="0"
            step="0.01"
          />
        </div>

        <div class="form-row">
          <div class="form-group">
            <label class="form-label">Start Date</label>
            <input 
              v-model="form.startDate" 
              type="date" 
              class="form-control"
            />
          </div>

          <div class="form-group">
            <label class="form-label">End Date</label>
            <input 
              v-model="form.endDate" 
              type="date" 
              class="form-control"
            />
          </div>
        </div>
      </form>
    </CreateModal>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import api from '@/services/api'
import { useAlert } from '@/plugins/sweetalert'
import DataTable from '@/components/data-table/DataTable.vue'
import CreateModal from '@/components/modals/CreateModal.vue'

export default {
  name: 'TestSeriesList',
  components: { DataTable, CreateModal },
  setup() {
    const route = useRoute()
    const alert = useAlert()
    const examId = route.params.examId

    const columns = [
      { key: 'name', label: 'Name', sortable: true, minWidth: '250px' },
      { key: 'tests', label: 'Tests', class: 'text-center' },
      { key: 'price', label: 'Price', sortable: true },
      { key: 'duration', label: 'Duration' },
      { key: 'status', label: 'Status', sortable: true },
      { key: 'actions', label: 'Actions', class: 'text-end' }
    ]

    const exam = ref(null)
    const testSeriesList = ref([])
    const loading = ref(true)

    // Modal state
    const showModal = ref(false)
    const isEditing = ref(false)
    const saving = ref(false)
    const editingId = ref(null)
    const form = reactive({
      name: '',
      description: '',
      totalTests: 0,
      price: 0,
      isFree: true,
      status: 'draft',
      startDate: '',
      endDate: ''
    })

    const breadcrumbs = computed(() => [
      { title: 'Dashboard', url: '/app/dashboard' },
      { title: 'Exams', url: '/app/exams/list' },
      { title: exam.value?.name || 'Exam', active: true }
    ])

    const fetchData = async () => {
      loading.value = true
      try {
        const [examRes, seriesRes] = await Promise.all([
          api.get(`/v1/admin/exams/${examId}`),
          api.get(`/v1/admin/exams/${examId}/testseries`)
        ])
        exam.value = examRes.data.exam
        testSeriesList.value = seriesRes.data.testSeries || []
      } catch (err) {
        console.error('Failed to fetch data:', err)
      } finally {
        loading.value = false
      }
    }

    const formatDate = (date) => {
      return new Date(date).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
      })
    }

    const getStatusClass = (status) => {
      const classes = {
        draft: 'status-inactive',
        active: 'status-active',
        archived: 'status-pending'
      }
      return classes[status] || 'status-inactive'
    }

    const resetForm = () => {
      form.name = ''
      form.description = ''
      form.totalTests = 0
      form.price = 0
      form.isFree = true
      form.status = 'draft'
      form.startDate = ''
      form.endDate = ''
      editingId.value = null
      isEditing.value = false
    }

    const openCreateModal = () => {
      resetForm()
      showModal.value = true
    }

    const openEditModal = (series) => {
      isEditing.value = true
      editingId.value = series.id
      form.name = series.name || ''
      form.description = series.description || ''
      form.totalTests = series.totalTests || 0
      form.price = series.price || 0
      form.isFree = series.isFree ?? true
      form.status = series.status || 'draft'
      form.startDate = series.startDate ? series.startDate.split('T')[0] : ''
      form.endDate = series.endDate ? series.endDate.split('T')[0] : ''
      showModal.value = true
    }

    const saveTestSeries = async () => {
      if (!form.name) return
      
      saving.value = true
      try {
        const payload = {
          testSeries: {
            name: form.name,
            description: form.description,
            totalTests: form.totalTests,
            price: form.isFree ? 0 : form.price,
            isFree: form.isFree,
            status: form.status,
            startDate: form.startDate || null,
            endDate: form.endDate || null,
            examId
          }
        }

        if (isEditing.value) {
          const response = await api.put(`/v1/admin/testseries/${editingId.value}`, payload)
          const index = testSeriesList.value.findIndex(s => s.id === editingId.value)
          if (index !== -1) {
            testSeriesList.value[index] = { ...testSeriesList.value[index], ...response.data.testSeries || payload.testSeries }
          }
          alert.success('Updated!', 'Test series updated successfully')
        } else {
          const response = await api.post(`/v1/admin/testseries`, payload)
          testSeriesList.value.unshift(response.data.testSeries || response.data)
          alert.success('Created!', 'Test series created successfully')
        }
        showModal.value = false
        resetForm()
      } catch (error) {
        alert.error('Error', error.response?.data?.message || 'Failed to save test series')
      } finally {
        saving.value = false
      }
    }

    const confirmDelete = async (series) => {
      const result = await alert.delete(
        'Delete Test Series?',
        `This will also delete all tests under "${series.name}".`
      )

      if (result.isConfirmed) {
        try {
          await api.delete(`/v1/admin/testseries/${series.id}`)
          testSeriesList.value = testSeriesList.value.filter(s => s.id !== series.id)
          alert.success('Deleted!', 'Test series has been deleted.')
        } catch (err) {
          alert.error('Error', 'Failed to delete test series')
        }
      }
    }

    onMounted(fetchData)

    return { 
      examId, 
      columns,
      exam, 
      testSeriesList, 
      loading,
      breadcrumbs,
      showModal,
      isEditing,
      saving,
      form,
      openCreateModal,
      openEditModal,
      saveTestSeries,
      formatDate, 
      getStatusClass, 
      confirmDelete 
    }
  }
}
</script>

<style scoped>
.modal-form {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-label {
  font-weight: 500;
  font-size: 0.875rem;
  color: #374151;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.form-control {
  padding: 0.625rem 0.875rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.875rem;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.form-control:focus {
  outline: none;
  border-color: #6366f1;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
}

.form-check {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.form-check-input {
  width: 1rem;
  height: 1rem;
  cursor: pointer;
}

.form-check-label {
  font-size: 0.875rem;
  color: #374151;
  cursor: pointer;
}

:global(body.dark-mode) .form-label {
  color: #d1d5db;
}

:global(body.dark-mode) .form-control {
  background: #1e293b;
  border-color: #374151;
  color: #f3f4f6;
}

:global(body.dark-mode) .form-check-label {
  color: #d1d5db;
}
</style>
